import type {
  Mutation,
  Query,
  QueryShipmentByIdArgs,
  QueryShipmentsForShipperArgs,
  Shipment,
} from 'graphql-models';
import { delay } from 'msw';
import type { SortDirection } from 'walden-fast/types/pagination';
import { consigneeFactory } from './mocks/box-backend/consignee';
import { filterOn } from './mocks/box-backend/filters';
import {
  batteryMeasurementFactory,
  locationMeasurementFactory,
  temperatureMeasurementFactory,
} from './mocks/box-backend/measurements';
import { slicedPaginatedData } from './mocks/box-backend/paginator-info';
import { shipmentFactory } from './mocks/box-backend/shipment';
import { factoryList } from './mocks/lists';
import { sortArrayByKeyAndDirection } from './sorting';

type QueryMethods =
  | Query
  | { shipmentById: (args: QueryShipmentByIdArgs) => Query['shipmentById'] }
  | {
      shipmentsForShipper: (
        args: QueryShipmentsForShipperArgs,
      ) => Promise<Query['shipmentsForShipper']>;
    };

type MutationMethods = Mutation;
// | {
//     // Add mutiation methods here
//   };

const CONSIGNEES = factoryList(
  5,
  consigneeFactory,
  Array.from({ length: 5 }, (_, i) => ({
    name: `Consignee ${i}`,
  })),
);

const SHIPMENTS = [
  shipmentFactory({
    updatedAt: new Date('Mon Feb 21 2025 15:15:35 GMT+0100').toISOString(),
    id: '111',
    state: 'DELIVERED',
    parcelId: 'SHIP-DEL-03240018',
    consignee: CONSIGNEES[0],
    currentLocation: locationMeasurementFactory({
      longitude: -1.057169,
      latitude: 45.916157,
    }),
    orderReference: '123456',
  }).forBox({
    id: '333',
  }),
  shipmentFactory({
    updatedAt: new Date('Mon Feb 21 2025 15:15:35 GMT+0100').toISOString(),
    id: '112',
    state: 'DELIVERED',
    parcelId: 'SHIP-DEL-03243303',
    minTemperature: 0,
    maxTemperature: 7.2,
    consignee: CONSIGNEES[1],
    currentTemperature: temperatureMeasurementFactory({
      valueCelsius: -3.6,
      measuredAt: new Date('Mon Feb 21 2025 15:15:35 GMT+0100').toISOString(),
    }),
    currentLocation: locationMeasurementFactory({
      longitude: 1.86856,
      latitude: 46.16914,
    }),
    orderReference: 'D3-239D-123456',
  }).forBox({
    id: '111',
    reportedLocked: true,
    reportedClosed: false,
  }),
  shipmentFactory({
    id: '113',
    state: 'SHIPPING_IN_PROGRESS',
    parcelId: 'SHIP-DEL-03240976',
    currentBattery: batteryMeasurementFactory({
      percent: 12.3,
    }),
    currentLocation: locationMeasurementFactory({
      longitude: 4.835659,
      latitude: 45.764042,
    }),
    orderReference: 'F4-239D',
  }).forBox({
    id: '222',
    reportedLocked: false,
  }),
  shipmentFactory({
    currentLocation: locationMeasurementFactory({
      longitude: 5.04148,
      latitude: 47.322048,
    }),
    orderReference: 'AAA4-239D',
  }),
  shipmentFactory({
    currentLocation: locationMeasurementFactory({
      longitude: 2.354314,
      latitude: 48.857705,
    }),
  }),
  shipmentFactory(),
  shipmentFactory(),
  shipmentFactory(),
  shipmentFactory(),
  shipmentFactory(),
  shipmentFactory(),
  shipmentFactory(),
  shipmentFactory(),
  shipmentFactory(),
  shipmentFactory(),
];

export const queryMethods: QueryMethods = {
  shipmentsForShipper: async ({ filter, page, first, orderBy }) => {
    await delay();
    const filtered = filterOn(SHIPMENTS, filter);
    const sorted = sortByOrderByClause<Shipment>(filtered, { orderBy, first });
    return slicedPaginatedData(sorted, {
      page: Number(page),
      first: Number(first),
    });
  },
  consignees: CONSIGNEES,
  shipmentById: ({ id }) =>
    SHIPMENTS.find((shipment) => shipment.externalUuid === id),
};

export const sortByOrderByClause = <Data>(
  data: Data[],
  { orderBy }: QueryShipmentsForShipperArgs,
) => {
  const sortKey = orderBy && snakeToCamel(orderBy.column);
  const sortDirection =
    orderBy && (orderBy.order.toLowerCase() as SortDirection);

  const sorted = sortArrayByKeyAndDirection(
    data,
    sortKey && sortDirection
      ? {
          sortKey,
          sortDirection,
        }
      : undefined,
  );
  return sorted;
};

// @ts-expect-error complains that not all mutations are implemented but they are going to change anyway
export const mutationMethods: MutationMethods = {};

function snakeToCamel(snakeStr: string) {
  return snakeStr
    .toLowerCase()
    .replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
}
